import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

export default function DMInbox() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dms/threads");
      setThreads(r.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh the inbox instantly whenever a new DM lands on this account —
  // no more waiting for a page refresh to see a fresh conversation surface.
  useEffect(() => {
    const onNewDM = () => { load(); };
    window.addEventListener("nw:new-dm", onNewDM);
    return () => window.removeEventListener("nw:new-dm", onNewDM);
  }, [load]);

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="dm-inbox">
        <div className="font-mono text-acid text-xs tracking-widest">// DMS</div>
        <h1 className="font-heading text-5xl md:text-7xl leading-none mt-2">WHISPERS.</h1>
        <p className="font-mono text-white/60 text-xs mt-3">// tap a member in any room to start a private thread</p>

        <div className="mt-10 border-t border-white/10">
          {loading && <div className="p-6 font-mono text-white/40">LOADING…</div>}
          {!loading && threads.length === 0 && (
            <div className="p-6 font-mono text-white/40 text-sm">// no whispers yet · go start one from a room</div>
          )}
          {threads.map((t) => {
            const other = t.with;
            const label = other?.username || "deleted-user";
            const avatar = other?.avatar || "/favicon.png";
            const to = other?.id ? `/dm/${other.id}` : "#";
            return (
              <Link key={t.thread} to={to}
                    data-testid={`dm-thread-${label}`}
                    className={`flex items-center gap-4 p-4 border-b border-white/10 hover:bg-panel ${other ? "" : "pointer-events-none opacity-50"}`}>
                <img src={avatar} alt="" className="w-10 h-10 object-cover border border-white/10" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">@{label}</div>
                  <div className="font-body text-white/60 text-sm truncate">{t.last?.body}</div>
                </div>
                <MessageCircle className="w-4 h-4 text-white/30" />
              </Link>
            );
          })}
        </div>
      </main>
    </div>
  );
}
